import { Container, DisplayObject, Graphics, Point, Sprite, Texture } from 'pixi.js';
import { GridObject, GridSystem } from './GridSystem';
import { Layers, Layer, LayerSystem } from '@rendering/LayerSystem';

export interface FurnitureDefinition {
  id: string;
  name: string;
  gridWidth: number;
  gridHeight: number;
  spriteAsset: string;
  cost: number;
  category: FurnitureCategory;
  description: string;
}

export type FurnitureCategory = 
  | 'seating' 
  | 'tables' 
  | 'bedroom' 
  | 'medical' 
  | 'storage' 
  | 'decoration';

export type FurnitureRotation = 0 | 90 | 180 | 270;

export interface FurniturePlacementResult {
  furniture: GridObject[];
  rejectedPositions: Point[];
}

export class FurnitureSystem {
  private readonly gridSystem: GridSystem;
  private readonly layerSystem: LayerSystem;
  private readonly container: Container;
  private readonly furnitureDefinitions: Map<string, FurnitureDefinition> = new Map();

  constructor(gridSystem: GridSystem, layerSystem: LayerSystem, container: Container) {
    this.gridSystem = gridSystem;
    this.layerSystem = layerSystem;
    this.container = container;
    this.initializeFurnitureDefinitions();
  }

  private initializeFurnitureDefinitions(): void {
    // Seating furniture
    this.registerFurniture({
      id: 'armchair',
      name: 'Armchair',
      gridWidth: 1,
      gridHeight: 1,
      spriteAsset: 'furniture/armchair.png',
      cost: 250,
      category: 'seating',
      description: 'Standard seating, conversation areas'
    });

    this.registerFurniture({
      id: 'loveseat',
      name: 'Loveseat',
      gridWidth: 2,
      gridHeight: 1,
      spriteAsset: 'furniture/loveseat.png',
      cost: 400,
      category: 'seating',
      description: 'Couples seating, social interaction'
    });

    this.registerFurniture({
      id: 'sofa_3seat',
      name: 'Sofa (3-seat)',
      gridWidth: 3,
      gridHeight: 1,
      spriteAsset: 'furniture/sofa_3seat.png',
      cost: 600,
      category: 'seating',
      description: 'Group seating, TV area, social spaces'
    });

    // Tables
    this.registerFurniture({
      id: 'side_table',
      name: 'Side Table',
      gridWidth: 1,
      gridHeight: 1,
      spriteAsset: 'furniture/side_table.png',
      cost: 75,
      category: 'tables',
      description: 'Next to seating, personal items, lamps'
    });

    this.registerFurniture({
      id: 'coffee_table',
      name: 'Coffee Table',
      gridWidth: 2,
      gridHeight: 1,
      spriteAsset: 'furniture/coffee_table.png',
      cost: 150,
      category: 'tables',
      description: 'Living area centerpiece, magazine storage'
    });

    this.registerFurniture({
      id: 'dining_table_2p',
      name: 'Dining Table (2-person)',
      gridWidth: 2,
      gridHeight: 2,
      spriteAsset: 'furniture/dining_table_2p.png',
      cost: 300,
      category: 'tables',
      description: 'Intimate dining, couples'
    });

    // Bedroom furniture
    this.registerFurniture({
      id: 'bed_twin',
      name: 'Standard Bed (Twin)',
      gridWidth: 1,
      gridHeight: 3,
      spriteAsset: 'furniture/bed_twin.png',
      cost: 500,
      category: 'bedroom',
      description: 'Single occupancy, space efficient'
    });

    this.registerFurniture({
      id: 'bed_full',
      name: 'Standard Bed (Full)',
      gridWidth: 2,
      gridHeight: 3,
      spriteAsset: 'furniture/bed_full.png',
      cost: 750,
      category: 'bedroom',
      description: 'Couples, premium rooms'
    });

    this.registerFurniture({
      id: 'dresser',
      name: 'Dresser',
      gridWidth: 2,
      gridHeight: 1,
      spriteAsset: 'furniture/dresser.png',
      cost: 200,
      category: 'bedroom',
      description: 'Clothing storage, personal items'
    });

    this.registerFurniture({
      id: 'nightstand',
      name: 'Nightstand',
      gridWidth: 1,
      gridHeight: 1,
      spriteAsset: 'furniture/nightstand.png',
      cost: 100,
      category: 'bedroom',
      description: 'Bedside essentials, lamp, phone'
    });

    // Medical equipment
    this.registerFurniture({
      id: 'hospital_bed',
      name: 'Hospital Bed',
      gridWidth: 1,
      gridHeight: 3,
      spriteAsset: 'furniture/hospital_bed.png',
      cost: 1200,
      category: 'medical',
      description: 'Adjustable height, electric controls, safety rails'
    });

    this.registerFurniture({
      id: 'wheelchair',
      name: 'Wheelchair',
      gridWidth: 1,
      gridHeight: 1,
      spriteAsset: 'furniture/wheelchair.png',
      cost: 300,
      category: 'medical',
      description: 'Resident mobility, foldable storage'
    });
  }

  public registerFurniture(definition: FurnitureDefinition): void {
    this.furnitureDefinitions.set(definition.id, definition);
  }

  public getFurnitureDefinition(id: string): FurnitureDefinition | undefined {
    return this.furnitureDefinitions.get(id);
  }

  public getAllFurniture(): FurnitureDefinition[] {
    return Array.from(this.furnitureDefinitions.values());
  }

  public getFurnitureByCategory(category: FurnitureCategory): FurnitureDefinition[] {
    return this.getAllFurniture().filter(f => f.category === category);
  }

  public canPlaceFurniture(
    furnitureId: string,
    gridX: number,
    gridY: number,
    floor: number = 0,
    rotation: FurnitureRotation = 0
  ): boolean {
    // console.log('🔍 Checking if furniture can be placed:', { furnitureId, gridX, gridY, floor, rotation });

    const definition = this.furnitureDefinitions.get(furnitureId);
    if (!definition) {
      console.log('❌ No definition found for furniture ID:', furnitureId);
      return false;
    }

    // Get rotated dimensions
    const { width, height } = this.getRotatedDimensions(definition, rotation);
    // console.log('📏 Checking area with dimensions:', { width, height });

    // Check if all required floor tiles exist and are free
    for (let x = 0; x < width; x++) {
      for (let y = 0; y < height; y++) {
        const checkX = gridX + x;
        const checkY = gridY + y;

        // console.log(`🔍 Checking tile at (${checkX}, ${checkY})`);

        // Must have a floor tile at this position
        const hasFloor = this.gridSystem.hasFloor(checkX, checkY, floor);
        if (!hasFloor) {
          // console.log(`❌ No floor tile at (${checkX}, ${checkY})`);
          return false;
        }

        // Check if any furniture already occupies this space
        const hasFurniture = this.gridSystem.hasFurniture(checkX, checkY, floor);
        if (hasFurniture) {
          // console.log(`❌ Furniture already at (${checkX}, ${checkY})`);
          return false;
        }
      }
    }

    // console.log('✅ All checks passed, furniture can be placed');
    return true;
  }

  private getRotatedDimensions(definition: FurnitureDefinition, rotation: FurnitureRotation): { width: number, height: number } {
    // At 90° and 270°, width and height are swapped
    if (rotation === 90 || rotation === 270) {
      return { width: definition.gridHeight, height: definition.gridWidth };
    }
    return { width: definition.gridWidth, height: definition.gridHeight };
  }

  public getFurnitureAt(gridX: number, gridY: number, floor: number = 0): GridObject | null {
    return this.gridSystem.getObjectByType(gridX, gridY, floor, 'furniture') || null;
  }

  public placeFurniture(
    furnitureId: string,
    gridX: number,
    gridY: number,
    floor: number = 0,
    rotation: FurnitureRotation = 0
  ): GridObject | null {
    console.log('🛋️ FurnitureSystem.placeFurniture called:', { furnitureId, gridX, gridY, floor, rotation });

    if (!this.canPlaceFurniture(furnitureId, gridX, gridY, floor, rotation)) {
      console.log('❌ canPlaceFurniture returned false');
      return null;
    }

    const definition = this.furnitureDefinitions.get(furnitureId);
    if (!definition) {
      console.log('❌ No definition found for furniture:', furnitureId);
      return null;
    }

    // Get rotated dimensions
    const { width, height } = this.getRotatedDimensions(definition, rotation);
    console.log('📐 Placing furniture with dimensions:', { width, height });

    // Create the main furniture object at the anchor position
    const furniture: GridObject = {
      id: furnitureId,
      type: 'furniture',
      gridX,
      gridY,
      floor,
      width,
      height
    };

    console.log('🏗️ Creating sprites for furniture footprint...');

    // For multi-tile furniture, place a sprite on each tile it occupies
    for (let dx = 0; dx < width; dx++) {
      for (let dy = 0; dy < height; dy++) {
        const tileX = gridX + dx;
        const tileY = gridY + dy;
        
        console.log(`🎨 Creating sprite for tile (${tileX}, ${tileY})`);
        
        // Create the sprite for this tile
        const worldPos = this.gridSystem.gridToWorld(tileX, tileY);
        const sprite = this.createFurnitureSprite(definition, rotation);
        sprite.position.set(worldPos.x, worldPos.y);
        
        console.log(`🌍 Sprite positioned at world coords: (${worldPos.x}, ${worldPos.y})`);
        
        // Add to the appropriate layer
        this.layerSystem.add(sprite, 'Furniture', new Point(tileX, tileY));
        console.log(`📚 Sprite added to Furniture layer`);
        
        // For the main anchor tile, store the sprite reference
        if (dx === 0 && dy === 0) {
          furniture.sprite = sprite;
          console.log(`🔗 Main sprite reference stored`);
        }
      }
    }

    // Register in grid system at each occupied position
    for (let dx = 0; dx < width; dx++) {
      for (let dy = 0; dy < height; dy++) {
        const tileX = gridX + dx;
        const tileY = gridY + dy;
        
        // Create a furniture object for each tile it occupies
        const tileObject: GridObject = {
          id: `${furnitureId}_${tileX}_${tileY}`,
          type: 'furniture',
          gridX: tileX,
          gridY: tileY,
          floor,
          width: 1, // Each tile is 1x1
          height: 1
        };
        
        this.gridSystem.setObjectByType(tileX, tileY, floor, 'furniture', tileObject);
        console.log(`📍 Furniture tile registered at (${tileX}, ${tileY})`);
      }
    }
    
    console.log('✅ All furniture tiles registered in grid system');

    return furniture;
  }

  public removeFurniture(gridX: number, gridY: number, floor: number = 0): GridObject | null {
    const furniture = this.gridSystem.getObjectByType(gridX, gridY, floor, 'furniture');
    if (!furniture) {
      return null;
    }

    console.log(`🗑️ Removing furniture: ${furniture.id} at (${gridX}, ${gridY})`);

    // Parse the furniture ID to get the base furniture type 
    // The ID format could be: "coffee_table" (single tile) or "coffee_table_22_14" (multi-tile with coordinates)
    let baseFurnitureId = furniture.id;
    
    // Check if this looks like a tile-specific ID (ends with coordinates)
    const idParts = furniture.id.split('_');
    if (idParts.length >= 3) {
      // Check if the last two parts are numbers (coordinates)
      const lastPart = idParts[idParts.length - 1];
      const secondLastPart = idParts[idParts.length - 2];
      
      if (!isNaN(Number(lastPart)) && !isNaN(Number(secondLastPart))) {
        // This is a tile ID, remove the coordinate parts to get the base furniture ID
        baseFurnitureId = idParts.slice(0, -2).join('_');
        console.log(`📝 Detected tile ID, base furniture: ${baseFurnitureId}`);
      }
    }
    
    // Get furniture definition to determine dimensions
    const definition = this.furnitureDefinitions.get(baseFurnitureId);
    if (!definition) {
      console.log('❌ No definition found for furniture:', baseFurnitureId);
      console.log('📋 Available furniture types:', Array.from(this.furnitureDefinitions.keys()));
      return null;
    }

    // If this is a tile-specific ID (has coordinates), we need to find the main anchor position
    let anchorX = gridX;
    let anchorY = gridY;
    
    if (baseFurnitureId !== furniture.id) {
      // This is a tile ID like "coffee_table_22_14"
      // We need to find all tiles that belong to the SAME furniture instance
      
      // Strategy: Find all adjacent tiles of the same furniture type
      // A furniture piece consists of contiguous tiles with the same base ID
      const connectedTiles = this.findConnectedFurnitureTiles(baseFurnitureId, gridX, gridY, floor);
      console.log(`🔗 Found ${connectedTiles.length} connected tiles for this furniture instance`);
      
      if (connectedTiles.length > 0) {
        // Find the top-left corner (minimum X and Y) of THIS specific furniture instance
        anchorX = Math.min(...connectedTiles.map((tile: GridObject) => tile.gridX));
        anchorY = Math.min(...connectedTiles.map((tile: GridObject) => tile.gridY));
        console.log(`📍 Found anchor position: (${anchorX}, ${anchorY}) from connected tiles`);
      }
    }

    const { width, height } = { width: definition.gridWidth, height: definition.gridHeight };
    console.log(`📐 Removing furniture with dimensions: ${width}x${height}`);

    // Get the furniture layer container
    const furnitureLayer = this.layerSystem.getLayer('Furniture');
    
    // Remove ALL sprites and grid objects that this furniture occupies
    for (let dx = 0; dx < width; dx++) {
      for (let dy = 0; dy < height; dy++) {
        const tileX = anchorX + dx;
        const tileY = anchorY + dy;
        
        // Remove from grid system
        this.gridSystem.removeObjectByType(tileX, tileY, floor, 'furniture');
        console.log(`📍 Furniture tile removed from grid at (${tileX}, ${tileY})`);
        
        // Remove sprite
        const worldPos = this.gridSystem.gridToWorld(tileX, tileY);
        const spritesToRemove: DisplayObject[] = [];
        
        furnitureLayer.children.forEach((sprite) => {
          if (Math.abs(sprite.position.x - worldPos.x) < 1 && 
              Math.abs(sprite.position.y - worldPos.y) < 1) {
            spritesToRemove.push(sprite);
          }
        });
        
        spritesToRemove.forEach((sprite) => {
          furnitureLayer.removeChild(sprite);
          sprite.destroy();
        });
      }
    }

    console.log('✅ Furniture completely removed');
    
    // Return a reconstruction of the main furniture object for undo/redo purposes
    return {
      id: baseFurnitureId,
      type: 'furniture',
      gridX: anchorX,
      gridY: anchorY,
      floor,
      width,
      height
    };
  }

  public getAllPlacedFurniture(): GridObject[] {
    const allObjects = this.gridSystem.getAllObjects();
    return allObjects.filter((obj: GridObject) => obj.type === 'furniture');
  }

  public createPreviewSprite(furnitureId: string): Sprite | null {
    const definition = this.furnitureDefinitions.get(furnitureId);
    if (!definition) {
      return null;
    }

    const sprite = this.createFurnitureSprite(definition);
    sprite.alpha = 0.7;
    return sprite;
  }

  private createFurnitureSprite(definition: FurnitureDefinition, rotation: FurnitureRotation = 0): Sprite {
    // Use the exact same approach as floor tiles - create a diamond base texture
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d')!;
    canvas.width = GridSystem.TILE_WIDTH;
    canvas.height = GridSystem.TILE_HEIGHT;

    // Draw the isometric diamond base (same as floor tiles)
    const categoryColors = {
      seating: '#8B4513', // Brown
      tables: '#D2B48C',  // Tan
      bedroom: '#CD853F', // Peru
      medical: '#FFFFFF', // White
      storage: '#A0522D', // Sienna
      decoration: '#32CD32' // Lime green
    };

    const color = categoryColors[definition.category] || '#888888';
    const colorRGB = this.hexToRgb(color);
    
    // Draw diamond shape (same polygon as floor tiles)
    context.fillStyle = color;
    context.beginPath();
    context.moveTo(0, GridSystem.TILE_HEIGHT / 2);
    context.lineTo(GridSystem.TILE_WIDTH / 2, 0);
    context.lineTo(GridSystem.TILE_WIDTH, GridSystem.TILE_HEIGHT / 2);
    context.lineTo(GridSystem.TILE_WIDTH / 2, GridSystem.TILE_HEIGHT);
    context.closePath();
    context.fill();

    // Add a subtle border for definition
    context.strokeStyle = this.darkenColor(color, 20);
    context.lineWidth = 1;
    context.stroke();

    // Add rotation indicator (small arrow or triangle)
    this.drawRotationIndicator(context, rotation);

    // Add furniture type label in the center
    context.fillStyle = '#000000';
    context.font = 'bold 10px Arial';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    
    // Add white stroke for text readability
    context.strokeStyle = '#FFFFFF';
    context.lineWidth = 3;
    context.strokeText(definition.name.split(' ')[0], GridSystem.TILE_WIDTH / 2, GridSystem.TILE_HEIGHT / 2);
    context.fillText(definition.name.split(' ')[0], GridSystem.TILE_WIDTH / 2, GridSystem.TILE_HEIGHT / 2);

    const texture = Texture.from(canvas);
    const sprite = new Sprite(texture);
    sprite.anchor.set(0.5, 1); // Same anchor as floor tiles
    
    return sprite;
  }

  private findConnectedFurnitureTiles(baseFurnitureId: string, startX: number, startY: number, floor: number): GridObject[] {
    const visited = new Set<string>();
    const connectedTiles: GridObject[] = [];
    const queue = [{x: startX, y: startY}];
    
    while (queue.length > 0) {
      const {x, y} = queue.shift()!;
      const key = `${x},${y}`;
      
      if (visited.has(key)) continue;
      visited.add(key);
      
      const furniture = this.gridSystem.getObjectByType(x, y, floor, 'furniture');
      if (!furniture) continue;
      
      // Check if this tile belongs to the same furniture type
      const tileFurnitureId = furniture.id.split('_').slice(0, -2).join('_');
      if (tileFurnitureId !== baseFurnitureId) continue;
      
      connectedTiles.push(furniture);
      
      // Check adjacent tiles (4-directional)
      const neighbors = [
        {x: x + 1, y: y},
        {x: x - 1, y: y},
        {x: x, y: y + 1},
        {x: x, y: y - 1}
      ];
      
      for (const neighbor of neighbors) {
        const neighborKey = `${neighbor.x},${neighbor.y}`;
        if (!visited.has(neighborKey)) {
          const neighborFurniture = this.gridSystem.getObjectByType(neighbor.x, neighbor.y, floor, 'furniture');
          if (neighborFurniture) {
            const neighborBaseFurnitureId = neighborFurniture.id.split('_').slice(0, -2).join('_');
            if (neighborBaseFurnitureId === baseFurnitureId) {
              queue.push(neighbor);
            }
          }
        }
      }
    }
    
    return connectedTiles;
  }

  private drawRotationIndicator(context: CanvasRenderingContext2D, rotation: FurnitureRotation): void {
    const centerX = GridSystem.TILE_WIDTH / 2;
    const centerY = GridSystem.TILE_HEIGHT / 2;
    const size = 8;

    context.fillStyle = '#FFFFFF';
    context.strokeStyle = '#000000';
    context.lineWidth = 1;

    // Draw a small triangle pointing in the rotation direction
    context.save();
    context.translate(centerX, centerY - 12); // Position above center
    context.rotate((rotation * Math.PI) / 180);

    context.beginPath();
    context.moveTo(0, -size);
    context.lineTo(-size/2, size/2);
    context.lineTo(size/2, size/2);
    context.closePath();
    context.fill();
    context.stroke();

    context.restore();
  }

  private hexToRgb(hex: string): {r: number, g: number, b: number} {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return {r, g, b};
  }

  private darkenColor(color: string, percent: number): string {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);

    const newR = Math.max(0, Math.floor(r * (100 - percent) / 100));
    const newG = Math.max(0, Math.floor(g * (100 - percent) / 100));
    const newB = Math.max(0, Math.floor(b * (100 - percent) / 100));

    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  }

  private lightenColor(color: string, percent: number): string {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);

    const newR = Math.min(255, Math.floor(r + (255 - r) * percent / 100));
    const newG = Math.min(255, Math.floor(g + (255 - g) * percent / 100));
    const newB = Math.min(255, Math.floor(b + (255 - b) * percent / 100));

    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  }
}